import type { Message, Part } from "@mux/sdk/v2/client"
import { cmpMessage, sortParts } from "./message-reducer"

export type OptimisticStore = {
  message: Record<string, Message[] | undefined>
  part: Record<string, Part[] | undefined>
}
export type OptimisticAddInput = { sessionID: string; message: Message; parts: Part[] }
export type OptimisticRemoveInput = { sessionID: string; messageID: string }
export type OptimisticItem = { message: Message; parts: Part[] }
export type MessagePage = {
  session: Message[]
  part: { id: string; part: Part[] }[]
  cursor?: string
  complete: boolean
}

export const sortOptimisticItem = (item: OptimisticItem): OptimisticItem => ({
  message: item.message,
  parts: sortParts(item.parts),
})

const hasParts = (parts: Part[] | undefined, want: Part[]) =>
  parts ? want.every((part) => parts.some((item) => item.id === part.id)) : want.length === 0

const mergeParts = (parts: Part[] | undefined, want: Part[]) => {
  if (!parts) return sortParts(want)
  const next = [...parts]
  let changed = false
  for (const part of want) {
    if (next.some((item) => item.id === part.id)) continue
    next.push(part)
    changed = true
  }
  return changed ? sortParts(next) : parts
}

export function mergeOptimisticPage(page: MessagePage, items: OptimisticItem[]) {
  if (items.length === 0) return { ...page, confirmed: [] as string[] }
  const session = [...page.session]
  const part = new Map(page.part.map((item) => [item.id, sortParts(item.part)]))
  const confirmed: string[] = []
  for (const item of items) {
    const index = session.findIndex((message) => message.id === item.message.id)
    const found = index !== -1
    if (!found) {
      const insert = session.findIndex((message) => cmpMessage(message, item.message) > 0)
      session.splice(insert === -1 ? session.length : insert, 0, item.message)
    }
    const current = part.get(item.message.id)
    if (found && hasParts(current, item.parts)) confirmed.push(item.message.id)
    else part.set(item.message.id, mergeParts(current, item.parts))
  }
  return {
    cursor: page.cursor,
    complete: page.complete,
    session,
    part: [...part.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([id, value]) => ({ id, part: value })),
    confirmed,
  }
}

export function applyOptimisticAdd(draft: OptimisticStore, input: OptimisticAddInput) {
  const messages = draft.message[input.sessionID]
  if (!messages) draft.message[input.sessionID] = [input.message]
  else {
    // Adding a message the list already holds replaces it: each ID appears once.
    const existing = messages.findIndex((message) => message.id === input.message.id)
    if (existing !== -1) messages.splice(existing, 1)
    const insert = messages.findIndex((message) => cmpMessage(message, input.message) > 0)
    messages.splice(insert === -1 ? messages.length : insert, 0, input.message)
  }
  draft.part[input.message.id] = sortParts(input.parts)
}

export function applyOptimisticRemove(draft: OptimisticStore, input: OptimisticRemoveInput) {
  const messages = draft.message[input.sessionID]
  const index = messages?.findIndex((message) => message.id === input.messageID) ?? -1
  if (messages && index !== -1) messages.splice(index, 1)
  delete draft.part[input.messageID]
}
